"use client";

import { useState, useEffect, type ComponentType } from "react";
import { useAuth } from "./Components/AuthProvider";
import OnboardingTour from "./Components/OnboardingTour";
import { useOnboardingTour } from "./hooks/useOnboardingTour";

// Landing components (for guests)
import LandingHome from "./Components/landing/home/LandingHome";
import Footer from "./Components/landing/Footer";
import { PageLoading } from './Components/LoadingBubbles';

function DashboardLoading() {
  return <PageLoading title="Loading your dashboard" description="Preparing your schedule and academic overview." />;
}

export default function Home() {
  const { user, loading: authLoading } = useAuth();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const [auditFixture, setAuditFixture] = useState<string>();
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development' || process.env.NEXT_PUBLIC_ENABLE_AUDIT_FIXTURES !== '1') return;
    const value = new URLSearchParams(window.location.search).get('auditFixture');
    if (value && ['dashboard-refresh-populated', 'dashboard-refresh-empty', 'dashboard-refresh-stale', 'dashboard-refresh-loading'].includes(value)) setAuditFixture(value);
  }, []);
  const [Dashboard, setDashboard] = useState<ComponentType<{ auditFixture?: string }> | null>(null);

  const {
    showTour,
    completeTour,
    dismissTour
  } = useOnboardingTour();

  useEffect(() => {
    let isCurrent = true;

    if (!user && !auditFixture) {
      setDashboard(null);
      return () => {
        isCurrent = false;
      };
    }

    void import("./Components/Dashboard/Dashboard").then(({ default: DashboardComponent }) => {
      if (isCurrent) {
        setDashboard(() => DashboardComponent);
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [user, auditFixture]);

  // Keep the route in one stable loading state while the session is being
  // restored. Rendering the guest landing before auth resolves causes a
  // noticeable homepage -> dashboard flash for signed-in users.
  if (!mounted || authLoading || user || auditFixture) {
    return (
      <div>
        {user && showTour && (
          <OnboardingTour
            isVisible={showTour}
            onComplete={completeTour}
            onSkip={dismissTour}
          />
        )}
        {(user || auditFixture) && Dashboard ? <Dashboard auditFixture={auditFixture} /> : <DashboardLoading />}
      </div>
    );
  }

  return (
    <div>
      <LandingHome />
      <div className="ss-deferred-landing-section"><Footer /></div>
    </div>
  );
}
